/**
 * Per-dancer privacy toggles and the physical columns they govern.
 * Re-exported by `view-fields.ts`; import from there.
 */

import type {
  DancerVisibilityPreferences,
  MediaTypeKey,
  ProfileFieldKey,
} from './view-fields.types';

const PREFERENCE_DEFAULTS: Record<keyof DancerVisibilityPreferences, boolean> =
  {
    bio_public: true,
    social_public: true,
    resume_public: true,
    measurements_public: false,
    contact_public: false,
  };

/**
 * Field → preference toggle map. Fields not listed here have no
 * per-dancer preference — they pass through the registry unchanged.
 */
const FIELD_PREFERENCE_KEY: Partial<
  Record<ProfileFieldKey, keyof DancerVisibilityPreferences>
> = {
  bio: 'bio_public',
  contactEmail: 'contact_public',
  contactPhone: 'contact_public',
  heightCm: 'measurements_public',
  weightKg: 'measurements_public',
  bustCm: 'measurements_public',
  waistCm: 'measurements_public',
  hipsCm: 'measurements_public',
  clothingSize: 'measurements_public',
  shoeSize: 'measurements_public',
  pointeShoeBrand: 'measurements_public',
  pointeShoeSize: 'measurements_public',
  instagramHandle: 'social_public',
  tiktokHandle: 'social_public',
  facebookUsername: 'social_public',
  youtubeUsername: 'social_public',
  vimeoHandle: 'social_public',
  pinterestHandle: 'social_public',
  snapchatHandle: 'social_public',
  xHandle: 'social_public',
  website: 'social_public',
  portfolioUrl: 'social_public',
};

export const MEDIA_PREFERENCE_KEY: Partial<
  Record<MediaTypeKey, keyof DancerVisibilityPreferences>
> = {
  resume: 'resume_public',
};

// ==================== Physical column backing ====================

/**
 * The three profile tables a `ProfileFieldKey` can be stored on.
 */
export type GatedColumnTable =
  | 'profiles'
  | 'professional_profiles'
  | 'artist_profiles';

export interface GatedColumn {
  table: GatedColumnTable;
  column: string;
}

/**
 * `ProfileFieldKey` → the physical column(s) it reads from.
 *
 * Why this is separate from `FIELD_PREFERENCE_KEY`: the registry keys are
 * camelCase display concepts, but every egress path (a SELECT projection, an
 * RSC flight payload, an AI tool response) deals in snake_case columns. Without
 * this table the toggle map cannot be checked against a projection, which is
 * how nine leaks of the same class shipped one at a time.
 *
 * Only fields that carry a per-dancer toggle need an entry. A key mapped to an
 * empty list is a deliberate "no column backs this" statement, not an omission.
 *
 * The camelCase key does NOT imply the column name. Two known divergences:
 *
 *  - `facebookUsername` / `youtubeUsername` each back TWO real columns on
 *    `professional_profiles`. `facebook_username` / `youtube_username` are the
 *    Meteor-import columns still read by `public-dancer-profile.service.ts`;
 *    `facebook_handle` / `youtube_handle` are what the dancer's own edit
 *    surface (`social-section.tsx`) and `atomic_profile_update` write, and what
 *    the `/dancers/[slug]` loaders read. Both are live, so both are gated —
 *    deriving one name from the registry key would have left the other open.
 *  - `contactEmail` has no column on any of the three tables. The public
 *    surface emits `contact_email: null` by construction; the address lives on
 *    `auth.users`, which is not anon-readable.
 */
const FIELD_COLUMNS: Partial<Record<ProfileFieldKey, readonly GatedColumn[]>> =
  {
    bio: [{ table: 'professional_profiles', column: 'bio' }],
    // No column backing — see the note above.
    contactEmail: [],
    contactPhone: [{ table: 'profiles', column: 'phone_number' }],
    heightCm: [{ table: 'artist_profiles', column: 'height_cm' }],
    weightKg: [{ table: 'artist_profiles', column: 'weight_kg' }],
    bustCm: [{ table: 'artist_profiles', column: 'bust_cm' }],
    waistCm: [{ table: 'artist_profiles', column: 'waist_cm' }],
    hipsCm: [{ table: 'artist_profiles', column: 'hips_cm' }],
    clothingSize: [{ table: 'artist_profiles', column: 'clothing_size' }],
    shoeSize: [
      { table: 'artist_profiles', column: 'shoe_size' },
      { table: 'artist_profiles', column: 'shoe_size_us' },
    ],
    pointeShoeBrand: [
      { table: 'artist_profiles', column: 'pointe_shoe_brand' },
    ],
    pointeShoeSize: [{ table: 'artist_profiles', column: 'pointe_shoe_size' }],
    instagramHandle: [
      { table: 'professional_profiles', column: 'instagram_handle' },
    ],
    tiktokHandle: [{ table: 'professional_profiles', column: 'tiktok_handle' }],
    facebookUsername: [
      { table: 'professional_profiles', column: 'facebook_handle' },
      { table: 'professional_profiles', column: 'facebook_username' },
    ],
    youtubeUsername: [
      { table: 'professional_profiles', column: 'youtube_handle' },
      { table: 'professional_profiles', column: 'youtube_username' },
    ],
    vimeoHandle: [{ table: 'professional_profiles', column: 'vimeo_handle' }],
    pinterestHandle: [
      { table: 'professional_profiles', column: 'pinterest_handle' },
    ],
    snapchatHandle: [
      { table: 'professional_profiles', column: 'snapchat_handle' },
    ],
    xHandle: [{ table: 'professional_profiles', column: 'x_handle' }],
    website: [{ table: 'professional_profiles', column: 'website' }],
    portfolioUrl: [{ table: 'professional_profiles', column: 'portfolio_url' }],
  };

function emptyToggleIndex(): Record<
  keyof DancerVisibilityPreferences,
  GatedColumn[]
> {
  return {
    bio_public: [],
    measurements_public: [],
    contact_public: [],
    social_public: [],
    resume_public: [],
  };
}

/**
 * Toggle → every physical column that toggle governs.
 *
 * DERIVED, never hand-written: the toggle assignment comes from
 * `FIELD_PREFERENCE_KEY` and the column names from `FIELD_COLUMNS`, so a field
 * cannot be gated by one map and ungated by the other (R4). `resume_public`
 * governs media rows in `artist_media`, not a profile column, so its list is
 * legitimately empty.
 */
export const GATED_COLUMNS_BY_TOGGLE: Readonly<
  Record<keyof DancerVisibilityPreferences, readonly GatedColumn[]>
> = (() => {
  const index = emptyToggleIndex();
  for (const [field, toggle] of Object.entries(FIELD_PREFERENCE_KEY) as [
    ProfileFieldKey,
    keyof DancerVisibilityPreferences,
  ][]) {
    for (const col of FIELD_COLUMNS[field] ?? []) {
      index[toggle].push(col);
    }
  }
  return index;
})();

/** Every gated column, flattened. */
export const GATED_COLUMNS: readonly GatedColumn[] = Object.values(
  GATED_COLUMNS_BY_TOGGLE,
).flat();

/**
 * Which toggle governs a column, by column name alone.
 *
 * Column names are unique across the three profile tables for every gated
 * field, so callers that only have a projection string (the egress detector,
 * a code review) can resolve the toggle without knowing the table.
 */
export function toggleForColumn(
  column: string,
): keyof DancerVisibilityPreferences | null {
  for (const [toggle, cols] of Object.entries(GATED_COLUMNS_BY_TOGGLE) as [
    keyof DancerVisibilityPreferences,
    readonly GatedColumn[],
  ][]) {
    if (cols.some((c) => c.column === column)) return toggle;
  }
  return null;
}

/**
 * Every `ProfileFieldKey` that carries a toggle, paired with that toggle.
 * Exposed so the detector can also match camelCase emission keys — AI tools
 * and mappers rename `height_cm` to `heightCm` on the way out, and that
 * rename is where several of the leaks lived.
 */
export const GATED_FIELD_KEYS: Readonly<
  Partial<Record<ProfileFieldKey, keyof DancerVisibilityPreferences>>
> = FIELD_PREFERENCE_KEY;

export function resolvePreference(
  prefs: DancerVisibilityPreferences | undefined,
  key: keyof DancerVisibilityPreferences,
): boolean {
  const v = prefs?.[key];
  return v ?? PREFERENCE_DEFAULTS[key];
}
